import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concat,
  EMPTY,
  endWith,
  exhaustMap,
  expand,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { selectUser } from 'src/app/core/store/selectors';
import { AppState } from 'src/app/core/store/state';
import { isPresent } from 'src/app/core/utils/isPresent';
import { ReportWorker } from 'src/app/core/workers/report.worker';
import { FileUploadApiService } from 'src/app/shared/services/file-upload.api-service';
import { ReportingApiService } from 'src/app/shared/services/reporting-api.service';
import { getFilenameFromContentDisposition } from 'src/app/shared/utils';
import { DialogsActions } from '../../dialogs/store/actions';
import { NotificationsActions } from '../../notifications/store/actions';
import { BatchUpdateDocumentDto, GetDocumentDto, GetDocumentDtoExtended } from '../models/documents.model';
import { DocumentsApiService } from '../services/documents-api.service';
import {
  DocumentProcessingApiService,
  DocumentProcessingPagedReviewResponse,
} from '../services/document-processing-api.service';
import { DocumentsActions } from './actions';
import { FileUploadResultDto } from 'src/app/shared/services/file-upload.api-service';
import { fromDocuments } from './selectors';

@Injectable()
export class FeatureEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private documentsApiService: DocumentsApiService,
    private documentProcessingApiService: DocumentProcessingApiService,
    private fileUploadApiService: FileUploadApiService,
    private reportingApiService: ReportingApiService,
    private dialogRef: MatDialog,
    private translocoService: TranslocoService,
    private reportWorker: ReportWorker,
  ) {}

  getDocuments$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(DocumentsActions.getDocuments))]).pipe(
      map(() => {
        return DocumentsActions.loadDocuments();
      }),
    ),
  );

  loadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.loadDocuments),
      switchMap(() => {
        return this.documentsApiService.getDocuments$().pipe(
          withLatestFrom(this.store.select(selectUser).pipe(filter(isPresent))),
          map(([documents, user]) => {
            const docsExtended: GetDocumentDtoExtended[] = [];
            documents.map((document: GetDocumentDto) => {
              docsExtended.push({
                ...document,
                counterparties: document.parties,
                myPosition: document.userPositionId,
              });
              return document;
            });

            return DocumentsActions.setDocuments({ documents: docsExtended });
          }),
          catchError((response) => {
            return of(DocumentsActions.loadDocumentsFail());
          }),
        );
      }),
    ),
  );

  downloadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.downloadDocument),
      mergeMap((props) => {
        return this.documentsApiService.downloadDocumentById$(props.uniqueId).pipe(
          map((response) => {
            const blob = new Blob([response.body!], { type: response.body!.type });
            const contentDisposition = response.headers.get('Content-Disposition');
            const filename =
              getFilenameFromContentDisposition(contentDisposition) || props.fileName;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            return DocumentsActions.downloadDocumentSuccess();
          }),
          catchError((response) => {
            return of(DocumentsActions.downloadDocumentFail());
          }),
        );
      }),
    ),
  );

  uploadSelectedDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.uploadSelectedDocumentFile),
      switchMap(({ file, requestKey }) =>
        this.fileUploadApiService.uploadFiles$([file]).pipe(
          map((uploadResponse) => {
            const uploadedFile = uploadResponse.results?.[0];

            if (!uploadedFile?.isSuccess || !uploadedFile.fileId || uploadedFile.validationErrors?.length) {
              return DocumentsActions.uploadSelectedDocumentFileFail({
                errorMessage: this.getDocumentUploadErrorMessage(file, uploadedFile),
                requestKey,
              });
            }

            return DocumentsActions.uploadSelectedDocumentFileSuccess({
              fileId: uploadedFile.fileId,
              requestKey,
            });
          }),
          catchError(() =>
            of(
              DocumentsActions.uploadSelectedDocumentFileFail({
                errorMessage: this.translocoService.translate('text.docAddingError', {
                  name: file.name,
                }),
                requestKey,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  createDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.createDocument),
      withLatestFrom(this.store.select(selectUser).pipe(filter(isPresent))),
      mergeMap(([props, user]) => {
        let model = { ...props.model };

        if (!model.properties.revisionOption) {
          model.properties = { ...model.properties, revisionOption: user.defaultRevisionOption };
        }

        return this.documentsApiService.createDocument$(model).pipe(
          map((document) => DocumentsActions.createDocumentSuccess({ document })),
          catchError(() => {
            return of(
              DocumentsActions.createDocumentFail({ document: model }),
              DocumentsActions.getDocuments(),
            );
          }),
        );
      }),
    ),
  );

  createDocumentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.createDocumentSuccess),
      map((props) => {
        this.store.dispatch(DocumentsActions.closeCreatingDocumentModal());
        const message = this.translocoService.translate('text.docAdded', {
          name: props.document.name,
        });

        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message,
          }),
        );

        this.dialogRef.closeAll();
        return DocumentsActions.getDocuments();
      }),
    ),
  );

  createDocumentFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.createDocumentFail),
      map((props) => {
        const message = this.translocoService.translate('text.docAddingError', {
          name: props.document.properties.name,
        });

        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.redCross,
            message,
          }),
        );

        return DocumentsActions.createDocumentCancelled();
      }),
    ),
  );

  createDocumentCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.createDocumentCancel),
      map((props) => {
        return DocumentsActions.createDocumentCancelled();
      }),
    ),
  );

  private getDocumentUploadErrorMessage(
    file: File,
    uploadedFile: FileUploadResultDto | undefined,
  ): string {
    if (uploadedFile?.validationErrors?.length) {
      return uploadedFile.validationErrors.join(' ');
    }

    if (uploadedFile?.errorMessage) {
      return uploadedFile.errorMessage;
    }

    return this.translocoService.translate('text.docAddingError', {
      name: file.name,
    });
  }

  updateDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.updateDocuments),
      mergeMap((props) => {
        return this.documentsApiService.batchUpdateDocuments$(props.documents).pipe(
          map(() => DocumentsActions.updateDocumentsSuccess()),
          catchError((response) => {
            return of(DocumentsActions.getDocuments(), DocumentsActions.updateDocumentsFail());
          }),
        );
      }),
    ),
  );

  updateDocumentsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.updateDocumentsSuccess),
      map(() => {
        return DocumentsActions.getDocuments();
      }),
    ),
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocument),
      map((props) => {
        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate('modalBody.confirmDeleteDocument'),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                DocumentsActions.deleteDocumentConfirmed({ uniqueId: props.uniqueId }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                DocumentsActions.deleteDocumentCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteDocumentConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentConfirmed),
      mergeMap((props) => {
        return this.documentsApiService.deleteDocument$(props.uniqueId).pipe(
          map(() => DocumentsActions.deleteDocumentSuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              DocumentsActions.getDocuments(),
              DocumentsActions.deleteDocumentFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteDocumentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentSuccess),
      map(() => {
        return DocumentsActions.getDocuments();
      }),
    ),
  );

  deleteDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocuments),
      map((props) => {
        const titleKey =
          props.uniqueIds?.length === 1
            ? 'modalBody.confirmDeleteDocument'
            : 'modalBody.confirmDeleteDocuments';

        return DialogsActions.showDialog({
          data: {
            actionsKeys: ['yes', 'no'],
            content: '',
            title: this.translocoService.translate(titleKey),
          },
          callback: (action) => {
            if (action === 'yes') {
              return this.store.dispatch(
                DocumentsActions.deleteDocumentsConfirmed({ uniqueIds: props.uniqueIds }),
              );
            } else {
              return of(
                DialogsActions.setRef({ ref: null }),
                DocumentsActions.deleteDocumentsCancel(),
              );
            }
          },
        });
      }),
    ),
  );

  deleteDocumentsConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentsConfirmed),
      mergeMap((props) => {
        return this.documentsApiService.batchDeleteDocument$(props.uniqueIds).pipe(
          map(() => DocumentsActions.deleteDocumentsSuccess()),
          catchError((response) => {
            return of(
              DialogsActions.setRef({ ref: null }),
              DocumentsActions.getDocuments(),
              DocumentsActions.deleteDocumentsFail(),
            );
          }),
        );
      }),
    ),
  );

  deleteDocumentsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.deleteDocumentsSuccess),
      map(() => {
        return DocumentsActions.getDocuments();
      }),
    ),
  );

  loadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.loadDocument),
      switchMap(({ uniqueId }) => {
        return this.documentsApiService.getDocumentById$(uniqueId).pipe(
          map((document) => DocumentsActions.setDocument({ document })),
          catchError((response) => {
            return of(DocumentsActions.loadDocumentFail());
          }),
        );
      }),
    ),
  );

  editDocumentCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.editDocumentCancel),
      map((props) => {
        return DocumentsActions.editDocumentCancelled();
      }),
    ),
  );

  editDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.editDocument),
      withLatestFrom(this.store.select(selectUser).pipe(filter(isPresent))),
      mergeMap(([props, user]) => {
        let model = { ...props.model };

        if (!model.properties.revisionOption) {
          model.properties = { ...model.properties, revisionOption: user.defaultRevisionOption };
        }

        const updateModel: BatchUpdateDocumentDto = {
          uniqueId: props.uniqueId,
          assignedPlaybookId: model.assignedPlaybookId,
          type: model.properties.type,
          userPositionId: model.properties.userPositionId,
          parties: model.properties.parties,
          status: model.properties.status,
          languageId: model.properties.languageId,
        };

        return this.documentsApiService.batchUpdateDocuments$([updateModel]).pipe(
          mergeMap((documents) => {
            const updatedDocument = documents?.[0];

            if (!updatedDocument) {
              return of(DocumentsActions.editDocumentFail(), DocumentsActions.getDocuments());
            }

            return of(
              DocumentsActions.editDocumentSuccess({
                document: updatedDocument,
                closeOnSuccess: props.closeOnSuccess,
              }),
            );
          }),
          catchError((response) => {
            return of(DocumentsActions.editDocumentFail(), DocumentsActions.getDocuments());
          }),
        );
      }),
    ),
  );

  editDocumentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.editDocumentSuccess),
      map((props) => {
        if (props.closeOnSuccess !== false) {
          this.store.dispatch(DocumentsActions.closeEditingDocumentModal());
          this.dialogRef.closeAll();
        }

        this.store.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.docUpdated', {
              name: props.document.name,
            }),
          }),
        );

        return DocumentsActions.getDocuments();
      }),
    ),
  );

  startPageReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.startPageReview),
      withLatestFrom(this.store.select(fromDocuments.selectActivePageReviewDocumentUniqueId)),
      exhaustMap(([props, activePageReviewDocumentUniqueId]) => {
        if (
          activePageReviewDocumentUniqueId &&
          activePageReviewDocumentUniqueId !== props.documentUniqueId
        ) {
          return EMPTY;
        }

        return concat(
          of(
            DocumentsActions.pageReviewStarted({
              documentUniqueId: props.documentUniqueId,
            }),
          ),
          this.getPageReviewPages$(props.documentUniqueId, props.assignedPlaybookId).pipe(
            map((pagedReviewData) =>
              DocumentsActions.pageReviewPageLoaded({
                documentUniqueId: props.documentUniqueId,
                pagedReviewData,
              }),
            ),
            endWith(
              DocumentsActions.pageReviewComplete({
                documentUniqueId: props.documentUniqueId,
              }),
            ),
            catchError(() =>
              of(
                DocumentsActions.pageReviewFail({
                  documentUniqueId: props.documentUniqueId,
                }),
              ),
            ),
          ),
        );
      }),
    ),
  );

  private getPageReviewPages$(documentUniqueId: string, assignedPlaybookId: string) {
    return this.documentProcessingApiService
      .pageReviewNoIncludes$(documentUniqueId, 1, assignedPlaybookId)
      .pipe(
        expand((pagedReviewData: DocumentProcessingPagedReviewResponse) => {
          if (
            pagedReviewData.nextPageNumber != null &&
            pagedReviewData.pageNumber !== pagedReviewData.totalPages &&
            pagedReviewData.totalPages !== 0
          ) {
            return this.documentProcessingApiService.pageReviewNoIncludes$(
              documentUniqueId,
              pagedReviewData.nextPageNumber,
              assignedPlaybookId,
            );
          }

          return EMPTY;
        }),
      );
  }

  sendReportCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.sendReportCancel),
      map((props) => {
        return DocumentsActions.sendReportCancelled();
      }),
    ),
  );

  sendReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.sendReport),
      mergeMap((props) => {
        return this.reportingApiService.sendReport$(props.report).pipe(
          map((response) => {
            this.reportWorker.init(response, props.doc);
            this.store.dispatch(DocumentsActions.closeSendingReportModal());
            this.store.dispatch(DocumentsActions.startAskingReportStatus());
            return DocumentsActions.sendReportStarted();
          }),
          catchError((response: HttpErrorResponse) => {
            console.log(response?.error || 'DocumentActions.sendReportFail');

            return of(DocumentsActions.sendReportCompleteFailed());
          }),
        );
      }),
    ),
  );
}
